import { useState, type FormEvent } from 'react';
import { problemMessage, useApi } from '../lib/api';

type Step = 'email' | 'code' | 'totp';

const input = 'w-full rounded border-2 border-ink bg-white px-3 py-2 font-mono';
const button =
  'rounded border-2 border-ink bg-benday px-4 py-2 font-semibold hover:bg-caption disabled:opacity-50';

/**
 * Staff sign-in: an email code, then (when the API requires it) a TOTP code. First-time staff set up an authenticator
 * here. The API decides who is staff; this page only walks through the steps.
 */
export function LoginPage({ needsMfa = false }: { needsMfa?: boolean }) {
  const { client, session } = useApi();
  const [step, setStep] = useState<Step>(needsMfa ? 'totp' : 'email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [totp, setTotp] = useState('');
  const [setup, setSetup] = useState<{ sharedKey: string; otpAuthUri: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function run(action: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (e) {
      setError(problemMessage(e));
    } finally {
      setBusy(false);
    }
  }

  const sendCode = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      const { data, error: err } = await client.POST('/auth/email/start', { body: { email } });
      if (err) throw err;
      if (data?.devCode) setCode(data.devCode);
      setStep('code');
    });
  };

  const verifyCode = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      const { data, error: err } = await client.POST('/auth/email/verify', {
        body: { email, code },
      });
      if (err || !data) throw err;
      session.set(data);
    });
  };

  const startSetup = () =>
    void run(async () => {
      const { data, error: err } = await client.POST('/auth/2fa/setup');
      if (err || !data) throw err;
      setSetup(data);
    });

  const verifyTotp = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      if (setup) {
        const { error: err } = await client.POST('/auth/2fa/enable', { body: { code: totp } });
        if (err) throw err;
      }
      const { data, error: err } = await client.POST('/auth/2fa/verify', { body: { code: totp } });
      if (err || !data) throw err;
      session.set(data);
    });
  };

  return (
    <div className="flex min-h-dvh items-center justify-center p-6">
      <div className="w-full max-w-sm rounded border-2 border-ink bg-white p-6 shadow-[6px_6px_0_var(--color-ink)]">
        <p className="mb-4 inline-block -rotate-3 bg-caption px-2 py-0.5 font-bang text-3xl tracking-wide">
          ARG Zombie
        </p>
        <h1 className="mb-4 text-xl font-bold">Admin sign-in</h1>

        {step === 'email' && (
          <form onSubmit={sendCode} className="space-y-3">
            <label className="block text-sm font-semibold" htmlFor="email">
              Email
            </label>
            <input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={input}
            />
            <button type="submit" disabled={busy} className={button}>
              Email me a code
            </button>
          </form>
        )}

        {step === 'code' && (
          <form onSubmit={verifyCode} className="space-y-3">
            <p className="text-sm">We sent a 6-digit code to {email}.</p>
            <label className="block text-sm font-semibold" htmlFor="code">
              Code
            </label>
            <input
              id="code"
              inputMode="numeric"
              required
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className={input}
            />
            <button type="submit" disabled={busy} className={button}>
              Sign in
            </button>
          </form>
        )}

        {step === 'totp' && (
          <form onSubmit={verifyTotp} className="space-y-3">
            <p className="text-sm">Admin tools need your authenticator code.</p>
            {setup ? (
              <div className="rounded border border-bruise bg-newsprint p-2 text-xs">
                <p>Add this key to your authenticator app, then enter the code it shows:</p>
                <p className="mt-1 font-mono break-all">{setup.sharedKey}</p>
              </div>
            ) : (
              <button type="button" onClick={startSetup} className="text-sm underline">
                First time? Set up an authenticator
              </button>
            )}
            <label className="block text-sm font-semibold" htmlFor="totp">
              Authenticator code
            </label>
            <input
              id="totp"
              inputMode="numeric"
              required
              value={totp}
              onChange={(e) => setTotp(e.target.value)}
              className={input}
            />
            <button type="submit" disabled={busy} className={button}>
              Verify
            </button>
          </form>
        )}

        {error && (
          <p role="alert" className="mt-4 rounded bg-blam/15 p-2 text-sm text-blam">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}
